// src/pages/CancerSecondOpinionHub.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Award, 
  CheckCircle2, 
  ArrowRight, 
  Sparkles, 
  Clock, 
  DollarSign, 
  FileText, 
  ChevronRight, 
  ChevronDown,
  Activity,
  Microscope,
  FileSearch,
  Dna,
  Scissors,
  ShieldAlert,
  Building2,
  Stethoscope,
  UploadCloud,
  HelpCircle,
  Users
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { SECOND_OPINION_HUB_DATA, SECOND_OPINION_SPOKES } from '@/data/secondOpinionData';
import Footer from '@/components/Footer';

export default function CancerSecondOpinionHub() {
  const [openFaq, setOpenFaq] = useState(null);
  const data = SECOND_OPINION_HUB_DATA;
  const pageUrl = `${SITE_ORIGIN}/cancer-second-opinion`;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'ShieldAlert': return <ShieldAlert className="w-6 h-6 text-emerald-700" />;
      case 'Microscope': return <Microscope className="w-6 h-6 text-emerald-700" />;
      case 'Scissors': return <Scissors className="w-6 h-6 text-emerald-700" />;
      case 'Dna': return <Dna className="w-6 h-6 text-emerald-700" />;
      case 'FileSearch': return <FileSearch className="w-6 h-6 text-emerald-700" />;
      case 'Activity': return <Activity className="w-6 h-6 text-emerald-700" />;
      default: return <CheckCircle2 className="w-6 h-6 text-emerald-700" />;
    }
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: data.title,
      description: data.description,
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
      author: {
        '@type': 'Person',
        name: data.reviewer.name,
        jobTitle: data.reviewer.title,
        description: data.reviewer.credentials,
      },
      reviewedBy: {
        '@type': 'Person',
        name: data.reviewer.name,
        jobTitle: data.reviewer.title,
      },
      lastReviewed: '2026-09-24',
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Cancer Care & Second Opinion Panel',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Cancer Second Opinion', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={data.metaTitle}
        description={data.description}
        path="/cancer-second-opinion"
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">Cancer Second Opinion</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to="/second-opinion" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Start Intake Brief <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-12 pb-16 bg-gradient-to-b from-emerald-50/60 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            NCCN Guideline Compliant · Multidisciplinary Review Board
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight">
            {data.h1}
          </h1>
          <p className="text-lg sm:text-2xl text-emerald-800 font-medium mb-6">
            {data.h1Sub}
          </p>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-charcoal/90 leading-relaxed mb-8">
            An independent second opinion from specialized NCI-trained oncologists, subspecialty oncopathologists, and cross-sectional radiologists. Ensure your diagnosis is correct, verify critical biomarker targets, and confirm the right treatment path before major surgery or systemic therapy.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link
              to="/second-opinion"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-semibold text-base transition shadow-md hover:shadow-lg"
            >
              Request Online Second Opinion <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#specialized-guides"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-gray-300 hover:border-forest-ink bg-white text-forest-ink font-semibold text-base transition"
            >
              Explore Specialty Spokes
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto text-left">
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Turnaround</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">12h Brief / 72h Panel</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Microscope className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Pathology</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">CAP-Accredited Review</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <FileSearch className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Radiology</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">Raw DICOM Over-Read</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Pricing</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">$450 Flat Upfront</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Medical Review Byline */}
      <section className="bg-white border-b border-border-mist py-5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-800 font-bold text-sm">
                MD
              </div>
              <div>
                <p className="font-semibold text-forest-ink">
                  Medically Reviewed by {data.reviewer.name}
                </p>
                <p className="text-gray-600 text-xs">
                  {data.reviewer.title} · {data.reviewer.credentials}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right text-gray-500 text-xs">
              <p>Updated: {data.reviewer.lastReviewed}</p>
              <p className="text-emerald-800 font-medium">{data.reviewer.guidelineRef}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* Section 1: When to Get a Second Opinion */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              When Should You Get a Cancer Second Opinion?
            </h2>
            <p className="text-charcoal/80 text-base">
              A second opinion is not about questioning your doctor’s dedication; it is standard clinical due diligence in complex oncology care.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {data.whenToGet.map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-white border border-border-mist hover:border-emerald-300 transition shadow-xs flex items-start gap-4">
                <div className="p-3 rounded-lg bg-emerald-50 flex-shrink-0">
                  {getIcon(item.icon)}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-forest-ink mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-charcoal/80 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: What Records to Prepare */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8 pb-6 border-b border-border-mist">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-2">
                What Records to Collect for Your Second Opinion
              </h2>
              <p className="text-charcoal/80 text-sm sm:text-base">
                Having your complete diagnostic records ready ensures rapid turnaround without unnecessary delays.
              </p>
            </div>
            <Link
              to="/second-opinion"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-forest-ink text-white font-medium text-sm hover:bg-forest-shadow transition flex-shrink-0"
            >
              <UploadCloud className="w-4 h-4" /> Upload Records
            </Link>
          </div>

          <div className="space-y-4">
            {data.recordsNeeded.map((rec, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-cream-paper/60 border border-border-mist">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-semibold text-forest-ink text-base">{rec.item}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      rec.badge === 'Mandatory' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {rec.badge}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-charcoal/80">{rec.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-emerald-50/80 border border-emerald-200 text-xs sm:text-sm text-emerald-900 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Need help retrieving records?</strong> ByOnco care coordinators can contact your hospital, obtain release authorizations, and manage overnight physical slide shipments on your behalf.
            </p>
          </div>
        </section>

        {/* Section 3: Comparison Table */}
        <section id="comparison-table">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Multidisciplinary Review vs. Standard Single-Doctor Consultation
            </h2>
            <p className="text-charcoal/80 text-base">
              Why an integrated tumor board panel provides superior clinical accuracy compared to traditional single-physician second opinions.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border-mist shadow-xs bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-forest-ink text-white font-medium text-xs sm:text-sm uppercase tracking-wider">
                <tr>
                  <th scope="col" className="px-6 py-4">Evaluation Dimension</th>
                  <th scope="col" className="px-6 py-4 bg-emerald-900">ByOnco Multidisciplinary Panel</th>
                  <th scope="col" className="px-6 py-4">Standard Hospital Second Opinion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-mist">
                {data.comparisonTable.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-cream-paper/40'}>
                    <td className="px-6 py-4 font-semibold text-forest-ink">
                      {row.feature}
                    </td>
                    <td className="px-6 py-4 font-medium text-emerald-900 bg-emerald-50/30">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{row.byonco}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {row.standard}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4: Pricing & Turnaround */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Transparent, Flat-Fee Pricing & Turnaround
            </h2>
            <p className="text-charcoal/80 text-base">
              No hidden facility fees, no surprise out-of-network hospital bills, and no multi-week scheduling queues.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {data.pricingComparison.map((tier, idx) => (
              <div 
                key={idx} 
                className={`rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition ${
                  tier.popular 
                    ? 'bg-white border-2 border-emerald-600 shadow-lg relative' 
                    : tier.traditional
                    ? 'bg-gray-50 border border-gray-200 text-gray-500'
                    : 'bg-white border border-border-mist shadow-xs'
                }`}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold tracking-wide uppercase">
                    Most Comprehensive
                  </span>
                )}
                <div>
                  <h3 className={`text-lg font-bold mb-1 ${tier.traditional ? 'text-gray-700' : 'text-forest-ink'}`}>
                    {tier.tier}
                  </h3>
                  <div className="my-4">
                    <span className={`text-3xl sm:text-4xl font-extrabold ${tier.traditional ? 'text-gray-700' : 'text-forest-ink'}`}>
                      {tier.price}
                    </span>
                    <p className={`text-xs font-medium mt-1 ${tier.popular ? 'text-emerald-700 font-semibold' : 'text-gray-500'}`}>
                      Turnaround: {tier.turnaround}
                    </p>
                  </div>

                  <ul className="space-y-3 my-6 text-xs sm:text-sm">
                    {tier.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          tier.popular ? 'text-emerald-600' : tier.traditional ? 'text-gray-400' : 'text-forest-ink'
                        }`} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-border-mist">
                  {tier.traditional ? (
                    <p className="text-xs text-center text-gray-500 italic">Reference comparison only</p>
                  ) : (
                    <Link
                      to={tier.ctaLink}
                      className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition ${
                        tier.popular 
                          ? 'bg-forest-ink hover:bg-forest-shadow text-white shadow-sm' 
                          : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-200'
                      }`}
                    >
                      {tier.ctaText} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 5: Step-by-Step Walkthrough */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              How the Online Second Opinion Process Works
            </h2>
            <p className="text-charcoal/80 text-base">
              Four streamlined steps from medical record submission to definitive tumor board recommendations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            <div className="p-5 rounded-xl bg-cream-paper/70 border border-border-mist relative">
              <span className="w-8 h-8 rounded-full bg-forest-ink text-white font-bold flex items-center justify-center text-sm mb-4">1</span>
              <h3 className="font-bold text-forest-ink mb-2">Upload Records</h3>
              <p className="text-xs text-charcoal/80 leading-relaxed">
                Submit pathology reports, treatment notes, and raw DICOM imaging via our secure HIPAA-compliant cloud portal.
              </p>
            </div>

            <div className="p-5 rounded-xl bg-cream-paper/70 border border-border-mist relative">
              <span className="w-8 h-8 rounded-full bg-forest-ink text-white font-bold flex items-center justify-center text-sm mb-4">2</span>
              <h3 className="font-bold text-forest-ink mb-2">Navigator Synthesis</h3>
              <p className="text-xs text-charcoal/80 leading-relaxed">
                An oncology clinical coordinator structures your health history, checks NCCN guideline milestones, and identifies data gaps.
              </p>
            </div>

            <div className="p-5 rounded-xl bg-cream-paper/70 border border-border-mist relative">
              <span className="w-8 h-8 rounded-full bg-forest-ink text-white font-bold flex items-center justify-center text-sm mb-4">3</span>
              <h3 className="font-bold text-forest-ink mb-2">Tumor Board Review</h3>
              <p className="text-xs text-charcoal/80 leading-relaxed">
                Subspecialist medical oncologists, oncopathologists, and radiologists independently examine your scans, slides, and genetics.
              </p>
            </div>

            <div className="p-5 rounded-xl bg-cream-paper/70 border border-border-mist relative">
              <span className="w-8 h-8 rounded-full bg-forest-ink text-white font-bold flex items-center justify-center text-sm mb-4">4</span>
              <h3 className="font-bold text-forest-ink mb-2">Comprehensive Report</h3>
              <p className="text-xs text-charcoal/80 leading-relaxed">
                Receive an exhaustive written treatment plan, clinical trial matches, and an optional 1-on-1 virtual oncology video consultation.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Specialized Spoke Directory */}
        <section id="specialized-guides">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
              Subspecialty Spoke Guides
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold font-serif text-forest-ink mt-3 mb-3">
              Explore Cancer Second Opinions by Diagnosis & Specialty
            </h2>
            <p className="text-charcoal/80 text-base">
              Dive into our dedicated clinical second opinion guides covering cancer subtypes, diagnostic pathology reviews, and radiology scan over-reads.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECOND_OPINION_SPOKES.map((spoke) => (
              <Link
                key={spoke.slug}
                to={spoke.path}
                className="group p-6 rounded-2xl bg-white border border-border-mist hover:border-emerald-600 hover:shadow-md transition flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md">
                      {spoke.shortName}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition" />
                  </div>
                  <h3 className="text-lg font-bold text-forest-ink group-hover:text-emerald-800 transition mb-2">
                    {spoke.h1}
                  </h3>
                  <p className="text-xs sm:text-sm text-charcoal/80 line-clamp-3 mb-4 leading-relaxed">
                    {spoke.clinicalSummary}
                  </p>
                </div>

                <div className="pt-4 border-t border-border-mist flex items-center justify-between text-xs text-gray-500">
                  <span>Reviewed by {spoke.reviewer.name.split('&')[0]}</span>
                  <span className="font-semibold text-emerald-800 group-hover:underline">Read Guide</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Section 7: In-Depth Clinical FAQs */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions About Cancer Second Opinions
            </h2>
            <p className="text-charcoal/80 text-base">
              Straightforward answers regarding insurance, physician relationships, pre-operative timing, and slide logistics.
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {data.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx}
                  className="rounded-xl border border-border-mist bg-white overflow-hidden transition shadow-xs"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 font-bold text-base sm:text-lg text-forest-ink hover:text-emerald-800 transition"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-emerald-700 flex-shrink-0 rotate-180 transition-transform" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-6 sm:px-6 text-sm sm:text-base text-charcoal/85 leading-relaxed border-t border-cream-paper bg-cream-paper/30">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 8: Final High-Converting Contextual CTA */}
        <section className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-12 text-center shadow-xl relative overflow-hidden">
          <div className="relative z-10 max-w-3xl mx-auto">
            <span className="inline-block px-3.5 py-1.5 rounded-full bg-emerald-800/60 text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
              Get Certainty Before You Treat
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold font-serif text-white mb-4 leading-tight">
              Validate Your Cancer Treatment Plan with Multidisciplinary Experts
            </h2>
            <p className="text-emerald-100/90 text-sm sm:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
              Don't face critical cancer treatment decisions with doubt. Upload your biopsy reports and imaging for an independent, fast-turnaround tumor board evaluation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/second-opinion"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-forest-ink hover:bg-emerald-50 font-bold text-base transition shadow-md"
              >
                Start Your Online Intake Brief <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/teleconsultation"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/30 hover:border-white text-white font-medium text-base transition"
              >
                Schedule Video Consult
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
